import React from "react";
import { WIZARD_QUESTIONS, WIZARD_SKIPPED } from "../data/exerciseWizardConfig";
import { formatPromptDateLabel } from "../utils/formatDateLabels";

const questionLookup = Object.fromEntries(WIZARD_QUESTIONS.map((question) => [question.id, question]));

function getOptionLabel(t, question, value) {
    const translated = t(`${question.optionKeyPrefix}.${value}`);
    return translated && translated !== `${question.optionKeyPrefix}.${value}` ? translated : value;
}

export default function ExerciseWizard({
    t,
    locale,
    targetDate,
    todayDate,
    wizardState,
    wizardHistory,
    onAnswer,
    onBack,
    onRemoveAnswer,
    onReset,
    onSkip,
    onGoToSearch,
    renderExerciseList,
}) {
    const question = wizardState?.nextQuestion || null;
    const answeredSteps = (wizardHistory || [])
        .map((questionId) => {
            if (questionId === "muscleGroup") {
                const value = wizardState?.answers?.[questionId];
                const translatedValue = t(`muscleGroups.${value}`);

                if (!value || value === WIZARD_SKIPPED) {
                    return null;
                }

                return {
                    id: questionId,
                    questionLabel: t("select.muscleGroup"),
                    valueLabel: translatedValue && translatedValue !== `muscleGroups.${value}` ? translatedValue : value,
                };
            }

            const step = questionLookup[questionId];
            const value = wizardState?.answers?.[questionId];

            if (!step || !value || value === WIZARD_SKIPPED) {
                return null;
            }

            return {
                id: questionId,
                questionLabel: t(step.titleKey),
                valueLabel: getOptionLabel(t, step, value),
            };
        })
        .filter(Boolean);

    const renderWizardActions = ({ className = "" } = {}) => (
        <div className={`wizardActions ${className}`.trim()}>
            <button
                type="button"
                className="appButton wizardActionButton"
                onClick={onBack}
                disabled={(wizardHistory || []).length === 0}
            >
                {t("select.wizard.back")}
            </button>
            {!wizardState.isComplete && (
                <button
                    type="button"
                    className="appButton wizardActionButton"
                    onClick={() => onSkip(question.id)}
                >
                    {t("select.wizard.skip")}
                </button>
            )}
            <button
                type="button"
                className="appButton wizardActionButton"
                onClick={onReset}
                disabled={(wizardHistory || []).length === 0}
            >
                {t("select.wizard.reset")}
            </button>
        </div>
    );

    return (
        <div className="wizardPanel">
            <div className="wizardHeader">
                <div>
                    <h2 className="wizardTitle">{t("select.wizard.title")}</h2>
                    <p className="wizardSubtitle">{t("select.wizard.subtitle")}</p>
                </div>
                <div className="wizardRemaining" aria-live="polite">
                    {t("select.wizard.remaining", { count: wizardState.remainingCount })}
                </div>
            </div>

            {targetDate && targetDate !== todayDate && (
                <div className="wizardDateNotice" data-testid="wizard-target-date-notice">
                    <span className="wizardDateNoticeIcon" aria-hidden="true">📅</span>
                    <span className="wizardDateNoticeLabel">
                        {t("select.wizard.targetDateNotice", {
                            date: formatPromptDateLabel(targetDate, locale) || targetDate,
                        })}
                    </span>
                </div>
            )}

            {answeredSteps.length > 0 && (
                <div className="wizardSummary">
                    <div className="wizardSummaryLabel">{t("select.wizard.answersLabel")}</div>
                    <div className="wizardSummaryList">
                        {answeredSteps.map((step) => (
                            <button
                                key={step.id}
                                type="button"
                                className="wizardSummaryItem"
                                onClick={() => onRemoveAnswer(step.id)}
                            >
                                <span className="wizardSummaryQuestion">{step.questionLabel}</span>
                                <span className="wizardSummaryValue">{step.valueLabel}</span>
                                <span className="wizardSummaryRemove" aria-hidden="true">x</span>
                            </button>
                        ))}
                    </div>
                </div>
            )}

            {wizardState.remainingCount === 0 ? (
                <div className="wizardEmptyState">
                    <p>{t("select.wizard.noResults")}</p>
                </div>
            ) : wizardState.isComplete ? (
                wizardState.remainingCount > 50 ? (
                    <div className="wizardTooManyResultsCard" role="status">
                        <div className="wizardNoticeIconWrap" aria-hidden="true">
                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <circle cx="11" cy="11" r="8" />
                                <line x1="21" y1="21" x2="16.65" y2="16.65" />
                            </svg>
                        </div>
                        <p className="wizardTooManyResultsText">
                            {t("select.wizard.tooManyResults", { count: wizardState.remainingCount })}
                        </p>
                        {onGoToSearch && (
                            <button
                                type="button"
                                className="appButton wizardGoToSearchButton"
                                onClick={onGoToSearch}
                            >
                                <svg
                                    width="16"
                                    height="16"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth="2.2"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    aria-hidden="true"
                                >
                                    <circle cx="11" cy="11" r="8" />
                                    <line x1="21" y1="21" x2="16.65" y2="16.65" />
                                </svg>
                                <span>{t("select.wizard.goToSearch")}</span>
                            </button>
                        )}
                    </div>
                ) : (
                    renderExerciseList(
                        wizardState.remainingExercises,
                        t("select.wizard.results")
                    )
                )
            ) : (
                <div className="wizardQuestionCard">
                    <h3 className="wizardQuestionTitle">{t(question.titleKey)}</h3>
                    <div className="wizardOptions" role="group" aria-label={t(question.titleKey)}>
                        {question.options.map((option) => (
                            <button
                                key={option.value}
                                type="button"
                                className="wizardOptionButton"
                                onClick={() => onAnswer(question.id, option.value)}
                            >
                                <span className="wizardOptionLabel">{getOptionLabel(t, question, option.value)}</span>
                                <span className="wizardOptionCount">{option.count}</span>
                            </button>
                        ))}
                    </div>
                </div>
            )}

            {renderWizardActions()}
        </div>
    );
}
